// ── Sessions section (Wave 9, REAL own-scoped reads + localized time) ───────
// Own previous + upcoming sessions for the participant's OWN cohort(s), with
// LOCALIZED times in the participant's timezone (their /auth/me zone, falling
// back to the session zone then the browser). Each row shows session/week number
// + recording availability (presence-only — the Recordings section has the
// detail).
//
// ISOLATION (FR-PDASH-001): sessions are read per the participant's OWN cohort
// (sessions.listForCohort on each own-cohort id) and re-filtered to the own-
// cohort set (ownSessionsOnly) as defense-in-depth — a peer/other-cohort row can
// NEVER render. REPORT-EXCLUSION (FR-REPORT-001): no report column, no report
// link, no report status; the recording flag is NOT a report.

import { CollectionPagination, CollectionToolbar, DataTableV2, IconCalendar } from "@vjal/ui";
import { useEffect, useMemo } from "react";

import { useHonestQuery } from "../../components/honest-state";
import type {
  CoachingCohortDto,
  CoachingSessionDto,
  ListResponse,
} from "../../services/coaching-dtos";
import { ownCohortIdSet, ownSessionsOnly } from "../../domain/participant-scope";
import { sessionStatusLabel } from "../sessions/sessions-helpers";
import { ParticipantSectionShell } from "./participant-section-shell";
import {
  orDash,
  participantQueryKeys,
  readOwnCohorts,
  readOwnCohortSessions,
} from "./participant-dashboard-helpers";
import {
  formatLocalizedSessionTime,
  resolveParticipantZone,
  sessionStartSortKey,
} from "./participant-time";
import {
  useParticipantSessionsCollection,
  type ParticipantSessionRow,
} from "./participant-sessions-collection";

export function ParticipantSessionsSection({ zone, onReady }: { zone: string; onReady?: (ready: boolean) => void }) {
  const cohortsQuery = useHonestQuery<ListResponse<CoachingCohortDto>>(
    participantQueryKeys.ownCohorts(),
    readOwnCohorts
  );
  const cohorts = useMemo(() => cohortsQuery.data?.items ?? [], [cohortsQuery.data]);
  const ownCohortIds = useMemo(() => ownCohortIdSet(cohorts), [cohorts]);
  const ownCohortIdList = useMemo(() => [...ownCohortIds].sort(), [ownCohortIds]);

  const sessionsQuery = useHonestQuery<ListResponse<CoachingSessionDto>>(
    participantQueryKeys.cohortSessionsForCohorts(ownCohortIdList),
    async () => {
      const responses = await Promise.all(ownCohortIdList.map((cohortId) => readOwnCohortSessions(cohortId)));
      const sessionsById = new Map<string, CoachingSessionDto>();
      for (const response of responses) {
        for (const session of response.items) {
          if (!sessionsById.has(session.id)) sessionsById.set(session.id, session);
        }
      }
      return { items: [...sessionsById.values()] };
    },
    { enabled: ownCohortIdList.length > 0, disabledPosture: "empty" }
  );
  const allRows = useMemo<ParticipantSessionRow[]>(() => {
    const ownSessions = ownSessionsOnly(sessionsQuery.data?.items, ownCohortIds);
    return [...ownSessions]
      .sort((a, b) => sessionStartSortKey(a.starts_at) - sessionStartSortKey(b.starts_at))
      .map((session, index) => ({
        id: session.id,
        title: orDash(session.title) === "—" ? `Session ${index + 1}` : String(session.title),
        cohort: orDash(session.cohort_name),
        when: formatLocalizedSessionTime(session.starts_at, resolveParticipantZone(zone, session.timezone)),
        startsAt: session.starts_at ?? "",
        status: sessionStatusLabel(session.status),
        statusRaw: session.status,
        recording: "See recordings",
      }));
  }, [ownCohortIds, sessionsQuery.data?.items, zone]);

  const collection = useParticipantSessionsCollection(allRows);
  useEffect(() => {
    if (!onReady) return;
    onReady(cohortsQuery.posture !== "loading" && sessionsQuery.posture !== "loading");
  }, [cohortsQuery.posture, onReady, sessionsQuery.posture]);
  const showToolbar = sessionsQuery.posture === null;

  if (cohortsQuery.posture !== null || ownCohortIdList.length === 0) {
    return (
      <ParticipantSectionShell
        title="Sessions"
        subtitle="Your previous and upcoming sessions, in your timezone."
        posture={cohortsQuery.posture ?? "empty"}
        gate={cohortsQuery.gate}
        reference={cohortsQuery.reference}
        onRetry={cohortsQuery.retry}
        emptyTitle="No sessions yet"
        emptyMessage="Your sessions will appear here once you're enrolled in a cohort."
        icon={<IconCalendar size={22} />}
        testId="participant-sessions-section"
      >
        <span />
      </ParticipantSectionShell>
    );
  }

  return (
    <ParticipantSectionShell
      title="Sessions"
      subtitle="Your previous and upcoming sessions, shown in your timezone."
      posture={sessionsQuery.posture}
      gate={sessionsQuery.gate}
      reference={sessionsQuery.reference}
      onRetry={sessionsQuery.retry}
      emptyTitle="No sessions scheduled"
      emptyMessage="Your sessions will appear here once they're scheduled for your cohort."
      testId="participant-sessions-section"
    >
      {showToolbar ? (
        <CollectionToolbar
          collection={collection}
          searchPlaceholder="Search by session, cohort, or time…"
          className="rounded-xl border border-line"
          stableChipNames
        />
      ) : null}
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Your sessions"
          emptyTitle="No sessions match your search"
          emptyMessage="Clear or change the filters above to widen the view."
        />
        {collection.uiState === "ready" ? <CollectionPagination collection={collection} className="px-4" /> : null}
      </div>
    </ParticipantSectionShell>
  );
}